import * as THREE from 'three';

/**
 * Blender-baked tileable ground materials packed into two texture arrays (detail albedo, normal),
 * so the terrain shader stays within the sampler limit.
 * Layers: 0 regolith, 1 trail, 2 duff, 3 rockface, 4 snow, 5 grassdry.
 * Albedo layers are brightness-normalized "detail" maps (mean ~0.5): the terrain multiplies its own
 * (imagery / strata) color by 2x the detail value.
 */
export const GROUND_LAYERS = ['regolith', 'trail', 'duff', 'rockface', 'snow', 'grassdry'] as const;

async function loadPixels(url: string, size: number): Promise<Uint8ClampedArray | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob(), { colorSpaceConversion: 'none', premultiplyAlpha: 'none', resizeWidth: size, resizeHeight: size, resizeQuality: 'high' });
    const c = new OffscreenCanvas(size, size);
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(bmp, 0, 0);
    return g.getImageData(0, 0, size, size).data;
  } catch {
    return null;
  }
}

export interface GroundSet {
  albedo: THREE.DataArrayTexture;
  normal: THREE.DataArrayTexture;
  tileMeters: number;
}

export async function loadGroundTextures(base: string, size = 1024): Promise<GroundSet | null> {
  const meta = await fetch(`${base}textures/ground/ground.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  if (!meta) return null;
  const n = GROUND_LAYERS.length;
  const alb = new Uint8Array(size * size * 4 * n);
  const nrm = new Uint8Array(size * size * 4 * n);
  let ok = 0;
  await Promise.all(GROUND_LAYERS.map(async (name, i) => {
    const get = async (stem: string) => (await loadPixels(`${base}textures/ground/${stem}.webp`, size))
      ?? (await loadPixels(`${base}textures/ground/${stem}.png`, size));
    const detail = (await get(`${name}_detail`)) ?? (await get(`${name}_albedo`));
    const normal = await get(`${name}_normal`);
    const L = size * size * 4;
    if (detail) {
      // normalize the mean to 0.5 so it acts as a pure detail modulator
      let sum = 0;
      for (let k = 0; k < L; k += 4) sum += detail[k] * 0.2126 + detail[k + 1] * 0.7152 + detail[k + 2] * 0.0722;
      const mean = sum / (size * size) / 255;
      const s = 0.5 / Math.max(mean, 0.05);
      for (let k = 0; k < L; k += 4) {
        alb[i * L + k] = Math.min(255, detail[k] * s);
        alb[i * L + k + 1] = Math.min(255, detail[k + 1] * s);
        alb[i * L + k + 2] = Math.min(255, detail[k + 2] * s);
        alb[i * L + k + 3] = 255;
      }
      ok++;
    } else {
      alb.fill(128, i * L, (i + 1) * L);
    }
    if (normal) nrm.set(normal, i * L);
    else for (let k = 0; k < L; k += 4) { nrm[i * L + k] = 128; nrm[i * L + k + 1] = 128; nrm[i * L + k + 2] = 255; nrm[i * L + k + 3] = 255; }
  }));
  if (!ok) return null;
  const mk = (data: Uint8Array) => {
    const t = new THREE.DataArrayTexture(data, size, size, n);
    t.format = THREE.RGBAFormat;
    t.type = THREE.UnsignedByteType;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = true;
    t.anisotropy = 8;
    t.needsUpdate = true;
    return t;
  };
  const tile = (meta.regolith?.tileMeters ?? meta.tileMeters ?? 2) as number;
  return { albedo: mk(alb), normal: mk(nrm), tileMeters: tile };
}

/** GLSL: detail sampling helpers for the terrain shader. */
export const groundGLSL = /* glsl */ `
uniform sampler2DArray uGroundAlb;
uniform sampler2DArray uGroundNrm;
uniform float uGroundOn;
uniform float uGroundTile;
// planar (top-down) sample with a rotated second octave to break repetition
vec3 groundDetail(vec2 p, float layer) {
  vec2 uv = p / uGroundTile;
  vec3 a = texture(uGroundAlb, vec3(uv, layer)).rgb;
  vec2 uv2 = mat2(0.8, 0.6, -0.6, 0.8) * p / (uGroundTile * 3.7) + 0.37;
  vec3 b = texture(uGroundAlb, vec3(uv2, layer)).rgb;
  return a * (0.75 + 0.5 * b);
}
vec3 groundNormalTS(vec2 p, float layer) {
  vec3 n = texture(uGroundNrm, vec3(p / uGroundTile, layer)).xyz * 2.0 - 1.0;
  n.y = -n.y;
  return n;
}
`;
